<div class="page-container">
  <div class="max-content-width spaced-sections">
    <div>
      <h1 class="page-title">Account overview</h1>
      <p class="page-subtitle">View and update your personal information.</p>
    </div>

    @if (profileSuccessMessage) {
      <div class="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
        {{ profileSuccessMessage }}
      </div>
    }

    @if (userInfo) {
      <section class="card card-padding">
        <div class="flex items-start justify-between gap-4">
          <div class="flex items-start gap-4">
            <div class="avatar-large">
              {{ this.getInitials() }}
            </div>

            <div class="min-w-0 flex-1">
              <p class="badge-primary">Your profile</p>
              <div class="flex items-center gap-3 mt-0.5">
                <h2 class="truncate text-2xl font-bold text-gray-900 -mb-1">
                  {{ userInfo.fullName }}
                </h2>
                <span
                  class="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-bold text-indigo-700 border border-indigo-200"
                >
                  {{ currentRole }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              (click)="openChangePassword()"
              class="rounded-lg border border-secondary/20 bg-white px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-secondary/5"
            >
              Change password
            </button>

            <button
              type="button"
              (click)="openEditProfile()"
              class="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
            >
              Edit
            </button>
          </div>
        </div>

        <div class="mt-6 grid gap-4 sm:grid-cols-2">
          <div class="info-box">
            <p class="info-box-label">Email</p>
            <p class="info-box-value break-all">
              {{ userInfo.email }}
            </p>
          </div>

          <div class="info-box">
            <p class="info-box-label">Affiliation</p>
            <p class="info-box-value">
              {{ userInfo.affiliation || 'No affiliation provided' }}
            </p>
          </div>
        </div>
      </section>
    }

    <section class="card card-padding">
      @if (isSuperAdmin) {
        <div class="mb-6 border-b border-red-100 pb-6">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div class="max-w-2xl">
              <p class="badge-danger">Danger zone</p>
              <h2 class="mt-2 text-lg font-bold text-gray-900">Transfer Super Administrator</h2>
              <p class="mt-1 text-sm text-gray-600">
                Assign another active user as Super Administrator. Your own Super Administrator role
                will be removed and you will need to sign in again.
              </p>
            </div>

            <div class="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
              <select
                class="input-field min-w-64"
                [disabled]="loadingAdminUsers || transferringSuperAdmin"
                [value]="superAdminTransferCandidate?.id || ''"
                (change)="selectSuperAdminTransferTarget($any($event.target).value)"
              >
                <option value="">
                  {{ loadingAdminUsers ? 'Loading users...' : 'Select user' }}
                </option>
                @for (target of superAdminTransferTargets; track target.id) {
                  <option [value]="target.id">
                    {{ formatAdminUserName(target) }} ({{ target.email }})
                  </option>
                }
              </select>

              <button
                type="button"
                class="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 text-nowrap"
                (click)="askTransferSuperAdmin()"
                [disabled]="!superAdminTransferCandidate || transferringSuperAdmin"
              >
                Transfer role
              </button>
            </div>
          </div>

          @if (superAdminTransferError) {
            <div
              class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {{ superAdminTransferError }}
            </div>
          }
        </div>
      }

      @if (!isSuperAdmin) {
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="max-w-2xl">
            <p class="badge-danger">Danger zone</p>
            <h2 class="mt-2 text-lg font-bold text-gray-900">Deactivate account</h2>
            <p class="mt-1 text-sm text-gray-600">
              Your account will be deactivated, your group and project memberships will be removed,
              and you will be signed out.
            </p>
          </div>

          <button
            type="button"
            (click)="askDeactivateAccount()"
            [disabled]="deactivatingAccount"
            class="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {{ deactivatingAccount ? 'Deactivating...' : 'Deactivate account' }}
          </button>
        </div>

        @if (deactivateAccountError) {
          <div
            class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {{ deactivateAccountError }}
          </div>
        }
      }
    </section>
  </div>
</div>

@if (userInfo) {
  <app-profile-edit-modal
    [open]="showEditModal"
    [userInfo]="userInfo"
    (openChange)="showEditModal = $event"
    (saved)="saveProfile($event)"
  ></app-profile-edit-modal>
}

@if (showChangePasswordModal) {
  <app-base-modal
    [open]="showChangePasswordModal"
    (openChange)="showChangePasswordModal = $event"
    title="Change password"
    size="lg"
  >
    <p class="mb-4 text-sm text-gray-600">Enter your old password and choose a new one.</p>

    <app-password-form
      [showCurrentPassword]="true"
      [loading]="changingPassword"
      [error]="changePasswordError"
      [success]="changePasswordSuccess"
      submitLabel="Save password"
      loadingLabel="Saving..."
      cancelLabel="Cancel"
      (passwordSubmit)="changePassword($event)"
      (canceled)="closeChangePassword()"
    ></app-password-form>
  </app-base-modal>
}

@if (!isSuperAdmin) {
  <app-confirm-modal
    [open]="showDeactivateAccountConfirm"
    [title]="'Deactivate account'"
    [message]="'Your account will be deactivated, your group and project memberships will be removed, and you will be signed out.'"
    [confirmText]="'Deactivate account'"
    [cancelText]="'Keep account'"
    [confirmVariant]="'danger'"
    [loading]="deactivatingAccount"
    size="md"
    (openChange)="showDeactivateAccountConfirm = $event"
    (confirmed)="deactivateAccount()"
    (canceled)="cancelDeactivateAccount()"
  ></app-confirm-modal>
}

@if (showSuperAdminTransferConfirm && superAdminTransferCandidate) {
  <app-base-modal
    [open]="showSuperAdminTransferConfirm"
    (openChange)="showSuperAdminTransferConfirm = $event"
    title="Transfer Super Administrator"
    size="md"
  >
    <div class="space-y-4">
      <app-base-alert
        type="warning"
        title="This changes system ownership"
        [message]="
          'The Super Administrator role will move to ' +
          formatAdminUserName(superAdminTransferCandidate) +
          '. Your own Super Administrator role will be removed immediately.'
        "
        [closeable]="false"
      ></app-base-alert>

      <div>
        <label for="superAdminTransferConfirm" class="input-label">
          Type {{ superAdminTransferCandidate.email }} to confirm
        </label>
        <input
          id="superAdminTransferConfirm"
          type="text"
          class="input-field mt-1"
          autocomplete="off"
          [value]="superAdminTransferConfirmation"
          (input)="superAdminTransferConfirmation = $any($event.target).value"
        />
      </div>

      <div>
        <label for="superAdminTransferPassword" class="input-label">
          Confirm with your current password
        </label>
        <input
          id="superAdminTransferPassword"
          type="password"
          class="input-field mt-1"
          autocomplete="current-password"
          [value]="superAdminTransferCurrentPassword"
          (input)="superAdminTransferCurrentPassword = $any($event.target).value"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2" data-modal-footer>
        <button type="button" class="btn-secondary" (click)="cancelTransferSuperAdmin()">
          Cancel
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
          [disabled]="!superAdminTransferFormValid || transferringSuperAdmin"
          (click)="transferSuperAdmin()"
        >
          {{ transferringSuperAdmin ? 'Transferring...' : 'Transfer Super Admin' }}
        </button>
      </div>
    </div>
  </app-base-modal>
}
